/* eslint-disable @typescript-eslint/no-explicit-any */
import { format } from "date-fns"
import { ptBR } from "date-fns/locale"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Separator } from "@/components/ui/separator"
import {
  Mail,
  User,
  FileText,
  Calendar,
  Clock,
  Package,
  Settings,
  CheckCircle,
  XCircle,
  AlertTriangle,
} from "lucide-react"

const TAX_TYPE_TRANSLATIONS: { [key: string]: string } = {
  CREATION: "Criação",
  UPDATE: "Atualização",
  DELETE: "Exclusão",
}

const TAX_TYPE_COLORS: { [key: string]: string } = {
  CREATION: "bg-green-100 text-green-700 border-green-200",
  UPDATE: "bg-blue-100 text-blue-700 border-blue-200",
  DELETE: "bg-red-100 text-red-700 border-red-200",
}

function InfoField({
  icon: Icon,
  label,
  value,
  className = "",
}: {
  icon: any
  label: string
  value: string
  className?: string
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-gray-600" />
        <span className="text-sm font-medium text-gray-700">{label}</span>
      </div>
      <div className={`p-3 bg-gray-50 rounded-lg border text-sm font-medium ${className}`}>{value}</div>
    </div>
  )
}

function StatusBadge({
  value,
  trueColor = "bg-green-100 text-green-700",
  falseColor = "bg-red-100 text-red-700",
}: {
  value: boolean
  trueColor?: string
  falseColor?: string
}) {
  return (
    <Badge variant="outline" className={`${value ? trueColor : falseColor} border-0 font-semibold`}>
      {value ? <CheckCircle className="h-3 w-3 mr-1" /> : <XCircle className="h-3 w-3 mr-1" />}
      {value ? "Sim" : "Não"}
    </Badge>
  )
}

export function ModalGNREDetails({ requestData }: { requestData: any }) {
  const taxTypeKey = requestData.tax_type_request
  const translatedTaxType = TAX_TYPE_TRANSLATIONS[taxTypeKey] || taxTypeKey

  return (
    <div className="space-y-6">
      {/* Informações Básicas */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <User className="h-5 w-5" />
            Informações Básicas
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InfoField icon={Mail} label="Email" className="flex-wrap break-all" value={requestData.email} />
            <InfoField icon={User} label="Responsável" value={requestData.responsible} />
          </div>
        </CardContent>
      </Card>

      {/* Detalhes da Solicitação */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <FileText className="h-5 w-5" />
            Detalhes da Solicitação
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <Settings className="h-4 w-4 text-gray-600" />
                <span className="text-sm font-medium text-gray-700">Tipo de Solicitação</span>
              </div>
              <Badge
                variant="outline"
                className={`${TAX_TYPE_COLORS[taxTypeKey] || "bg-gray-100 text-gray-700 border-gray-200"} w-fit px-3 py-2 text-sm font-semibold`}
              >
                {translatedTaxType}
              </Badge>
            </div>
            <InfoField icon={FileText} label="Tipo Fiscal" value={requestData.type} />
          </div>
        </CardContent>
      </Card>

      {/* Parâmetros da Solicitação */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Settings className="h-5 w-5" />
            Parâmetros da Solicitação
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Informações Temporais */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InfoField
                icon={Calendar}
                label="Período do Lote"
                value={`${requestData.batch_values.startDate} - ${requestData.batch_values.endDate}`}
              />
              <InfoField
                icon={Clock}
                label="Criado em"
                value={format(new Date(requestData.created_at), "dd/MM/yyyy HH:mm:ss", { locale: ptBR })}
              />
            </div>

            {/* Configurações da Geração */}
            {(requestData.batch_values.filterGenerated !== undefined ||
              requestData.batch_values.proceedDespiteWarning !== undefined) && (
                <>
                  <Separator />
                  <div className="space-y-6">
                    {requestData.batch_values.filterGenerated !== undefined && (
                      <div className="flex flex-col gap-3">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-gray-600" />
                          <span className="text-sm font-medium text-gray-700">Regerar guias processadas</span>
                          <StatusBadge
                            value={requestData.batch_values.filterGenerated}
                            trueColor="bg-green-100 text-green-700"
                            falseColor="bg-red-100 text-red-700"
                          />
                        </div>
                      </div>
                    )}

                    {requestData.batch_values.proceedDespiteWarning !== undefined && (
                      <>
                        {requestData.batch_values.filterGenerated !== undefined && <Separator />}
                        <div className="flex flex-col gap-3">
                          <div className="flex items-center gap-2">
                            <AlertTriangle className="h-4 w-4 text-gray-600" />
                            <span className="text-sm font-medium text-gray-700">
                              Gerou mesmo com alerta de guias existentes
                            </span>
                            <StatusBadge
                              value={requestData.batch_values.proceedDespiteWarning}
                              trueColor="bg-green-100 text-green-700"
                              falseColor="bg-red-100 text-red-700"
                            />
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </>
              )}
          </div>
        </CardContent>
      </Card>

      {/* Lotes */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Package className="h-5 w-5" />
            Lotes (
            {requestData.batch_values.batchNumbers.length > 0
              ? requestData.batch_values.batchNumbers[0].split(",").length
              : 0}
            )
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="border rounded-lg bg-gray-50 max-h-60 overflow-y-auto">
            {requestData.batch_values.batchNumbers.length > 0 ? (
              <div className="divide-y divide-gray-200">
                {requestData.batch_values.batchNumbers[0].split(",").map((batch: string, index: number) => (
                  <div key={index} className="p-4 hover:bg-gray-100 transition-colors duration-150">
                    <div className="flex items-center gap-2">
                      <Package className="h-4 w-4 text-gray-500" />
                      <span className="font-medium text-gray-900">{batch.trim()}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center">
                <Package className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-lg">Nenhum lote disponível.</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

    </div>
  )
}
